import { createSlice, createAsyncThunk } from '@reduxjs/toolkit'
import { adminAuthAPI, setAdminToken, clearAdminToken } from '../services/adminApi'

interface AdminUser {
  email: string
  role: string // 'admin' | 'superadmin'
  sub: string
}

interface AdminAuthState {
  adminUser: AdminUser | null
  adminToken: string | null
  adminRefreshToken: string | null
  isAdminAuthenticated: boolean
  loading: boolean
  error: string | null
  requiresPasswordChange: boolean
  passwordChangeSession: string | null
}

// Load initial state from sessionStorage
const loadFromLocalStorage = (): Partial<AdminAuthState> => {
  try {
    const adminToken = sessionStorage.getItem('adminToken')
    const adminRefreshToken = sessionStorage.getItem('adminRefreshToken')
    const adminUserStr = sessionStorage.getItem('adminUser')

    if (adminToken && adminUserStr) {
      return {
        adminUser: JSON.parse(adminUserStr),
        adminToken,
        adminRefreshToken,
        isAdminAuthenticated: true
      }
    }
  } catch (error) {
  }
  return {}
}

const initialState: AdminAuthState = {
  adminUser: null,
  adminToken: null,
  adminRefreshToken: null,
  isAdminAuthenticated: false,
  loading: false,
  error: null,
  requiresPasswordChange: false,
  passwordChangeSession: null,
  ...loadFromLocalStorage()
}

// Async thunks
export const adminLogin = createAsyncThunk(
  'adminAuth/login',
  async ({ email, password }: { email: string; password: string }, { rejectWithValue }) => {
    try {
      const response = await adminAuthAPI.login(email, password)

      // Check if password change is required
      if (response.challengeName === 'NEW_PASSWORD_REQUIRED') {
        return {
          requiresPasswordChange: true,
          session: response.session,
          message: response.message
        }
      }

      // Save to sessionStorage
      sessionStorage.setItem('adminToken', response.accessToken)
      sessionStorage.setItem('adminRefreshToken', response.refreshToken)
      sessionStorage.setItem('adminUser', JSON.stringify(response.admin))
      setAdminToken(response.accessToken)

      return {
        adminUser: response.admin,
        adminToken: response.accessToken,
        adminRefreshToken: response.refreshToken,
        requiresPasswordChange: false
      }
    } catch (error: any) {
      return rejectWithValue(error.response?.data?.message || 'Login failed')
    }
  }
)

export const completeNewPassword = createAsyncThunk(
  'adminAuth/completeNewPassword',
  async (
    { email, newPassword, session }: { email: string; newPassword: string; session: string },
    { rejectWithValue }
  ) => {
    try {
      const response = await adminAuthAPI.completeNewPassword(email, newPassword, session)

      // Save to sessionStorage
      sessionStorage.setItem('adminToken', response.accessToken)
      sessionStorage.setItem('adminRefreshToken', response.refreshToken)
      setAdminToken(response.accessToken)

      // Get admin user info (we need to fetch it)
      // For now, just use email
      const adminUser = { email, role: 'admin', sub: '' }
      sessionStorage.setItem('adminUser', JSON.stringify(adminUser))

      return {
        adminUser,
        adminToken: response.accessToken,
        adminRefreshToken: response.refreshToken
      }
    } catch (error: any) {
      return rejectWithValue(error.response?.data?.message || 'Failed to complete password change')
    }
  }
)

export const changeAdminPassword = createAsyncThunk(
  'adminAuth/changePassword',
  async ({ newPassword }: { newPassword: string }, { rejectWithValue }) => {
    try {
      const response = await adminAuthAPI.changePassword(newPassword)
      return response
    } catch (error: any) {
      return rejectWithValue(error.response?.data?.message || 'Failed to change password')
    }
  }
)

export const refreshAdminToken = createAsyncThunk(
  'adminAuth/refresh',
  async (_, { getState, rejectWithValue }) => {
    try {
      const state = getState() as { adminAuth: AdminAuthState }
      const refreshToken = state.adminAuth.adminRefreshToken

      if (!refreshToken) {
        throw new Error('No refresh token available')
      }

      const response = await adminAuthAPI.refreshToken(refreshToken)

      // Update sessionStorage
      sessionStorage.setItem('adminToken', response.accessToken)
      setAdminToken(response.accessToken)

      return {
        adminToken: response.accessToken
      }
    } catch (error: any) {
      return rejectWithValue(error.response?.data?.message || 'Failed to refresh token')
    }
  }
)

const adminAuthSlice = createSlice({
  name: 'adminAuth',
  initialState,
  reducers: {
    adminLogout: (state) => {
      state.adminUser = null
      state.adminToken = null
      state.adminRefreshToken = null
      state.isAdminAuthenticated = false
      state.error = null
      state.requiresPasswordChange = false
      state.passwordChangeSession = null
      clearAdminToken()
    },
    clearAdminError: (state) => {
      state.error = null
    }
  },
  extraReducers: (builder) => {
    // Login
    builder
      .addCase(adminLogin.pending, (state) => {
        state.loading = true
        state.error = null
      })
      .addCase(adminLogin.fulfilled, (state, action) => {
        state.loading = false
        if (action.payload.requiresPasswordChange) {
          state.requiresPasswordChange = true
          state.passwordChangeSession = action.payload.session
        } else {
          state.adminUser = action.payload.adminUser
          state.adminToken = action.payload.adminToken
          state.adminRefreshToken = action.payload.adminRefreshToken
          state.isAdminAuthenticated = true
          state.requiresPasswordChange = false
          state.passwordChangeSession = null
        }
      })
      .addCase(adminLogin.rejected, (state, action) => {
        state.loading = false
        state.error = action.payload as string
      })

    // Complete New Password
    builder
      .addCase(completeNewPassword.pending, (state) => {
        state.loading = true
        state.error = null
      })
      .addCase(completeNewPassword.fulfilled, (state, action) => {
        state.loading = false
        state.adminUser = action.payload.adminUser
        state.adminToken = action.payload.adminToken
        state.adminRefreshToken = action.payload.adminRefreshToken
        state.isAdminAuthenticated = true
        state.requiresPasswordChange = false
        state.passwordChangeSession = null
      })
      .addCase(completeNewPassword.rejected, (state, action) => {
        state.loading = false
        state.error = action.payload as string
      })

    // Change Password
    builder
      .addCase(changeAdminPassword.pending, (state) => {
        state.loading = true
        state.error = null
      })
      .addCase(changeAdminPassword.fulfilled, (state) => {
        state.loading = false
      })
      .addCase(changeAdminPassword.rejected, (state, action) => {
        state.loading = false
        state.error = action.payload as string
      })

    // Refresh Token
    builder
      .addCase(refreshAdminToken.pending, (state) => {
        state.loading = true
      })
      .addCase(refreshAdminToken.fulfilled, (state, action) => {
        state.loading = false
        state.adminToken = action.payload.adminToken
      })
      .addCase(refreshAdminToken.rejected, (state, action) => {
        state.loading = false
        state.error = action.payload as string
        // If refresh fails, log out
        state.adminUser = null
        state.adminToken = null
        state.adminRefreshToken = null
        state.isAdminAuthenticated = false
        clearAdminToken()
      })
  }
})

export const { adminLogout, clearAdminError } = adminAuthSlice.actions
export default adminAuthSlice.reducer
